import axios from 'axios';
import { API } from '../config/api';
import { clearLocalDrafts } from './offlineStore';
import { shouldPurgeAgentSession } from './sessionGuard';
import { clearStudentsCache } from './consignesCache';

let sessionPurgeStarted = false;

async function purgeAgentPhone() {
  if (sessionPurgeStarted) return;
  sessionPurgeStarted = true;
  try {
    await clearLocalDrafts();
  } catch (error) {
    /* IndexedDB indisponible */
  }
  clearStudentsCache();
  try {
    localStorage.removeItem('currentAgent');
    localStorage.removeItem('agentToken');
  } catch (error) {
    /* localStorage indisponible */
  }
  if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    window.location.assign('/');
  }
}

axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('agentToken');
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (error) {
    /* localStorage indisponible */
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    const headers = config.headers || {};
    let agentToken = '';
    try {
      agentToken = localStorage.getItem('agentToken') || '';
    } catch (storageError) {
      agentToken = '';
    }
    const authorization = headers.Authorization || headers.authorization || '';
    const hadAgentToken = Boolean(agentToken) && String(authorization).includes(agentToken);
    const hadAdminToken = Boolean(headers['X-Admin-Token'] || headers['x-admin-token']);
    if (shouldPurgeAgentSession(status, config.url, { hadAgentToken, hadAdminToken })) {
      purgeAgentPhone();
    }
    if (status === 401 && error?.response?.data?.detail === 'Session expirée' && hadAdminToken) {
      window.dispatchEvent(new CustomEvent('vatel-admin-expired'));
    }
    return Promise.reject(error);
  }
);

export const desktopApi = {
  isDesktopBridge: () => false,

  async getAuthConfig() {
    const response = await axios.get(`${API}/auth/config`);
    return response.data;
  },

  async loginAgent(username, password) {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    return response.data;
  },

  async logoutAgent() {
    const response = await axios.post(`${API}/auth/logout`);
    return response.data;
  },

  async changeOwnPassword(currentPassword, newPassword) {
    const response = await axios.post(`${API}/auth/password`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  async setAgentPassword(agentId, payload, adminToken) {
    const response = await axios.put(
      `${API}/admin/agents/${agentId}/password`,
      payload,
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async unlockAgent(agentId, adminToken) {
    const response = await axios.post(
      `${API}/admin/agents/${agentId}/unlock`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async getLoginAudit(adminToken, limit = 100) {
    const response = await axios.get(`${API}/admin/login-audit?limit=${limit}`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async getAgents(adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/agents`, { headers });
    return response.data;
  },

  async getManoeuvres(adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/manoeuvres`, { headers });
    return response.data;
  },

  async getReports(params = {}) {
    const { adminToken, withTotal, ...query } = params;
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const search = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
    });
    const response = await axios.get(`${API}/reports?${search.toString()}`, { headers });
    if (withTotal) {
      const total = Number(response.headers?.['x-total-count'] ?? response.data.length);
      return { items: response.data, total: Number.isFinite(total) ? total : response.data.length };
    }
    return response.data;
  },

  async getReportById(reportId) {
    const response = await axios.get(`${API}/reports/${reportId}`);
    return response.data;
  },

  async createReport(payload) {
    const response = await axios.post(`${API}/reports`, payload);
    return response.data;
  },

  async updateReport(reportId, payload) {
    const response = await axios.put(`${API}/reports/${reportId}`, payload);
    return response.data;
  },

  async getReportDraft(agentId) {
    const response = await axios.get(`${API}/report-drafts/${agentId}`);
    return response.data;
  },

  async getDraftOwnership(agentId, reportId) {
    const response = await axios.get(`${API}/report-drafts/${agentId}/ownership`, { params: { report_id: reportId } });
    return response.data;
  },

  async saveReportDraft(agentId, payload) {
    const response = await axios.put(`${API}/report-drafts/${agentId}`, payload);
    return response.data;
  },

  async transferReport(agentId, payload) {
    const response = await axios.post(`${API}/report-drafts/${agentId}/transfer`, payload);
    return response.data;
  },

  async deleteReportDraft(agentId, { abandon = false, reportId = '' } = {}) {
    const params = {};
    if (abandon) params.abandon = true;
    if (reportId) params.report_id = reportId;
    const response = await axios.delete(`${API}/report-drafts/${agentId}`, { params });
    return response.data;
  },

  async getReportHtml(reportId, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/reports/${reportId}/html`, { headers, responseType: 'text' });
    return response.data;
  },

  async uploadReportPhoto(reportId, file) {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post(`${API}/reports/${reportId}/photos`, form);
    return response.data;
  },

  async listReportPhotos(reportId) {
    const response = await axios.get(`${API}/reports/${reportId}/photos`);
    return response.data;
  },

  async getReportPhotoFile(reportId, photoId) {
    const response = await axios.get(`${API}/reports/${reportId}/photos/${photoId}/file`, { responseType: 'blob' });
    return response.data;
  },

  async deleteReportPhoto(reportId, photoId) {
    const response = await axios.delete(`${API}/reports/${reportId}/photos/${photoId}`);
    return response.data;
  },

  async createShareLink(reportId, hours) {
    const response = await axios.post(`${API}/reports/${reportId}/share`, hours ? { hours } : {});
    return response.data;
  },

  async getReportDocument(reportId, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/reports/${reportId}/pdf`, { headers, responseType: 'blob' });
    const cd = response.headers?.['content-disposition'] || '';
    const match = /filename=([^;]+)/i.exec(cd);
    const fallbackName = `rapport_${reportId}.pdf`;
    return {
      blob: new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' }),
      filename: match?.[1]?.replace(/"/g, '') || fallbackName,
    };
  },

  async verifyAdmin(password, username) {
    const body = { password };
    if (username && String(username).trim()) body.username = String(username).trim();
    const response = await axios.post(`${API}/admin/verify`, body);
    return response.data;
  },

  async getSetting(key, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/settings/${key}`, { headers });
    return response.data;
  },

  // Onglet Consignes : lecture avec la session de l'agent ou de l'admin.
  async getConsignes(adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.get(`${API}/consignes`, { headers });
    return response.data;
  },

  // Notes de suivi : jamais de nom dans l'URL (identifiant ou numéro de chambre seulement).
  async getStudentNotes(studentId) {
    const response = await axios.get(`${API}/students/${encodeURIComponent(studentId)}/notes`);
    return response.data.notes || [];
  },

  async getRoomNotes(room) {
    const response = await axios.get(`${API}/rooms/${encodeURIComponent(room)}/notes`);
    return response.data.notes || [];
  },

  // Rapports en cours (brouillons) qu'on peut lier à une note : les siens, tous pour un admin.
  async getNoteDraftLinks() {
    const response = await axios.get(`${API}/followup-notes/draft-links`);
    return response.data.drafts || [];
  },

  async createFollowupNote(note) {
    const response = await axios.post(`${API}/followup-notes`, note);
    return response.data;
  },

  async updateFollowupNote(noteId, note) {
    const response = await axios.put(`${API}/followup-notes/${encodeURIComponent(noteId)}`, note);
    return response.data;
  },

  async deleteFollowupNote(noteId) {
    const response = await axios.delete(`${API}/followup-notes/${encodeURIComponent(noteId)}`);
    return response.data;
  },

  async getStudents() {
    const response = await axios.get(`${API}/students`);
    return response.data;
  },

  async updateConsigneSection(sectionId, body, adminToken) {
    const response = await axios.put(
      `${API}/admin/consignes/${encodeURIComponent(sectionId)}`,
      { body },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async getStudentsAsAdmin(adminToken) {
    const response = await axios.get(`${API}/students`, { headers: { 'X-Admin-Token': adminToken } });
    return response.data;
  },

  async createStudent(student, adminToken) {
    const response = await axios.post(`${API}/admin/students`, student, { headers: { 'X-Admin-Token': adminToken } });
    return response.data;
  },

  async updateStudent(studentId, student, adminToken) {
    const response = await axios.put(
      `${API}/admin/students/${encodeURIComponent(studentId)}`,
      student,
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async deleteStudent(studentId, adminToken) {
    const response = await axios.delete(
      `${API}/admin/students/${encodeURIComponent(studentId)}`,
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async importStudents(payload, adminToken) {
    const response = await axios.post(`${API}/admin/students/import`, payload, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async getMigrationStatus(adminToken) {
    const response = await axios.get(`${API}/admin/db/migration-status`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async getAdminStatistics(adminToken) {
    const response = await axios.get(`${API}/admin/statistics`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async updateSetting(key, value, adminToken) {
    const response = await axios.put(
      `${API}/settings/${key}`,
      { value },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async createAgent(name, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.post(`${API}/agents`, { name }, { headers });
    return response.data;
  },

  async deleteAgent(agentId, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.delete(`${API}/agents/${agentId}`, { headers });
    return response.data;
  },

  async setAdminRights(accountId, enabled, adminToken) {
    const response = await axios.post(
      `${API}/admin/accounts/${accountId}/admin`,
      { enabled },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async setAgentAccess(accountId, enabled, adminToken) {
    const response = await axios.post(
      `${API}/admin/accounts/${accountId}/agent-access`,
      { enabled },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async deactivateAgent(agentId, adminToken) {
    const response = await axios.post(
      `${API}/admin/agents/${agentId}/deactivate`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async archiveAgent(agentId, adminToken) {
    const response = await axios.post(
      `${API}/admin/agents/${agentId}/archive`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async reactivateAgent(agentId, adminToken) {
    const response = await axios.post(
      `${API}/admin/agents/${agentId}/reactivate`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async revokeAgentSessions(agentId, adminToken) {
    const response = await axios.post(
      `${API}/admin/agents/${agentId}/revoke-sessions`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async createManoeuvre(name, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.post(`${API}/manoeuvres`, { name }, { headers });
    return response.data;
  },

  async deleteManoeuvre(manoeuvreId, adminToken) {
    const headers = adminToken ? { 'X-Admin-Token': adminToken } : {};
    const response = await axios.delete(`${API}/manoeuvres/${manoeuvreId}`, { headers });
    return response.data;
  },

  async deleteReport(reportId, adminToken) {
    const response = await axios.delete(`${API}/admin/reports/${reportId}`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async changeAdminPassword(currentPassword, newPassword, adminToken) {
    const response = await axios.post(
      `${API}/admin/change-password`,
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async getAppInfo() {
    const response = await axios.get(`${API}/app-info`);
    return response.data;
  },

  async getAuditLog(adminToken, limit = 100) {
    const response = await axios.get(`${API}/admin/audit-log?limit=${limit}`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async validateReports(adminToken) {
    const response = await axios.get(`${API}/admin/reports/validation`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async getMailSettings(adminToken) {
    const response = await axios.get(`${API}/admin/mail`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async updateMailSettings(payload, adminToken) {
    const body = typeof payload === 'boolean' ? { enabled: !!payload } : payload;
    const response = await axios.put(
      `${API}/admin/mail`,
      body,
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async addMailRecipient(address, adminToken) {
    const response = await axios.post(
      `${API}/admin/mail/recipients`,
      { address },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async updateMailRecipient(recipientId, address, adminToken) {
    const response = await axios.put(
      `${API}/admin/mail/recipients/${recipientId}`,
      { address },
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async deleteMailRecipient(recipientId, adminToken) {
    const response = await axios.delete(`${API}/admin/mail/recipients/${recipientId}`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  },

  async sendTestMail(adminToken, to = '') {
    const response = await axios.post(
      `${API}/admin/mail/test`,
      to ? { to } : {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async resendReportMail(reportId, adminToken) {
    const response = await axios.post(
      `${API}/admin/reports/${reportId}/mail/resend`,
      {},
      { headers: { 'X-Admin-Token': adminToken } }
    );
    return response.data;
  },

  async previewReportMail(reportId) {
    const response = await axios.get(`${API}/reports/${reportId}/mail/preview`);
    return response.data;
  },

  async sendReportMail(reportId) {
    const response = await axios.post(`${API}/reports/${reportId}/mail`, {});
    return response.data;
  },

  async exportMonthlyZip(adminToken, month) {
    const response = await axios.get(`${API}/admin/reports/monthly-zip?month=${encodeURIComponent(month)}`, {
      headers: { 'X-Admin-Token': adminToken },
      responseType: 'blob',
    });
    return {
      success: true,
      blob: response.data,
      filename: `vatel_export_${month}.zip`,
    };
  },

};

function mailWhen(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} à ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function mailStatusText(mail) {
  const status = mail?.status;
  const when = mailWhen(mail?.sent_at);
  const who = mail?.sent_by ? ` par ${mail.sent_by}` : '';
  if (status === 'sent' && mail?.kind === 'renvoi') {
    return when ? `Renvoi le ${when}${who}` : `Renvoi${who}`;
  }
  if (status === 'sent') {
    return when ? `Envoyé le ${when}${who}` : 'Envoyé à la liste de diffusion';
  }
  if (status === 'pending' && mail?.kind === 'renvoi') return 'Renvoi en cours';
  if (status === 'pending') return 'Envoi par e-mail en attente';
  if (status === 'failed' || status === 'disabled') {
    const reason = mail?.error || "L'envoi a échoué.";
    return /pdf/i.test(reason) ? reason : `${reason} Téléchargez le PDF.`;
  }
  return 'E-mail non envoyé';
}

export function mailFailureText(reason) {
  const text = typeof reason === 'string' && reason.trim() ? reason.trim() : "L'envoi a échoué.";
  return /pdf/i.test(text) ? text : `${text} Téléchargez le PDF.`;
}

export function mailOutcomeToast(toast, mail, fallbackError) {
  const status = mail?.status;
  if (status === 'sent' && mail?.kind === 'renvoi') {
    toast.success('Rapport renvoyé à la liste de diffusion', { duration: 8000 });
    return;
  }
  if (status === 'sent') {
    toast.success('Rapport envoyé à la liste de diffusion', { duration: 8000 });
    return;
  }
  if (status === 'pending') {
    toast.message('Envoi par e-mail en cours', { duration: 8000 });
    return;
  }
  toast.error(mailFailureText(mail?.error || fallbackError), { duration: 12000 });
}

export function mailSubmitMessage(mail, editMode = false) {
  if (editMode) return 'Rapport modifié avec succès';
  return "Rapport enregistré. Téléchargez le PDF ou envoyez-le par e-mail depuis l'historique.";
}